/* ═══════════════════════════════════════════════════════════
   PEPAX Shopify Theme — Global JavaScript
   Merged: theme.js + header.js + home-hero.js + social-carousel.js
   ═══════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  /* ═══════════════════════════════════════════════════════════
     1. BASE UTILITIES
     ═══════════════════════════════════════════════════════════ */

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function isDesktop() {
    return window.innerWidth >= 1024;
  }

  /* ═══════════════════════════════════════════════════════════
     2. SCROLL REVEAL ANIMATIONS
     ═══════════════════════════════════════════════════════════ */

  function initRevealAnimations() {
    var revealElements = document.querySelectorAll('.reveal');
    if (!revealElements.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach(function(el) { observer.observe(el); });
  }

  /* ═══════════════════════════════════════════════════════════
     3. HEADER DROPDOWN NAVIGATION
     ═══════════════════════════════════════════════════════════ */

  function initHeaderDropdowns() {
    var header = document.querySelector('[data-header]');
    var navBtns = document.querySelectorAll('[data-nav-btn]');
    var dropdowns = document.querySelectorAll('[data-dropdown]');
    var overlay = document.querySelector('[data-dropdown-overlay]');
    if (!navBtns.length || !dropdowns.length) return;

    var activeDropdown = null;
    var closeTimeout = null;
    var CLOSE_DELAY = 250;

    function openDropdown(index) {
      if (!isDesktop()) return;
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
      dropdowns.forEach(function(d) { d.classList.remove('active'); });
      navBtns.forEach(function(b) { b.setAttribute('aria-expanded', 'false'); });

      var target = document.querySelector('[data-dropdown="' + index + '"]');
      if (target) {
        target.classList.add('active');
        activeDropdown = index;
        if (overlay) overlay.classList.add('active');
        var btn = document.querySelector('[data-nav-btn="' + index + '"]');
        if (btn) btn.setAttribute('aria-expanded', 'true');
      }
    }

    function closeAllDropdowns() {
      dropdowns.forEach(function(d) { d.classList.remove('active'); });
      navBtns.forEach(function(b) { b.setAttribute('aria-expanded', 'false'); });
      if (overlay) overlay.classList.remove('active');
      activeDropdown = null;
    }

    function scheduleClose() {
      if (closeTimeout) clearTimeout(closeTimeout);
      closeTimeout = setTimeout(closeAllDropdowns, CLOSE_DELAY);
    }

    // Desktop hover / Mobile click for nav buttons
    navBtns.forEach(function(btn) {
      var index = btn.getAttribute('data-nav-btn');

      btn.addEventListener('mouseenter', function() {
        openDropdown(index);
      });

      btn.addEventListener('click', function(e) {
        if (!isDesktop()) {
          e.preventDefault();
          var targetDropdown = document.querySelector('[data-dropdown="' + index + '"]');
          if (targetDropdown) {
            var isOpen = targetDropdown.classList.contains('active');
            closeAllDropdowns();
            if (!isOpen) {
              targetDropdown.classList.add('active');
              activeDropdown = index;
            }
          }
        }
      });
    });

    // Keep dropdown open when hovering inside
    dropdowns.forEach(function(dd) {
      dd.addEventListener('mouseenter', function() {
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
      });
      dd.addEventListener('mouseleave', scheduleClose);
    });

    // Close when leaving header area
    if (header) {
      header.addEventListener('mouseleave', function() {
        if (isDesktop()) scheduleClose();
      });
    }

    // Overlay click closes everything
    if (overlay) {
      overlay.addEventListener('click', closeAllDropdowns);
    }

    // ESC closes dropdowns
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        closeAllDropdowns();
      }
    });

    // Resize: close dropdowns when crossing breakpoint
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function() {
      var w = window.innerWidth;
      if ((lastWidth < 1024 && w >= 1024) || (lastWidth >= 1024 && w < 1024)) {
        closeAllDropdowns();
      }
      lastWidth = w;
    });
  }

  /* ═══════════════════════════════════════════════════════════
     4. SHOP / ABOUT PREVIEW HOVER
     ═══════════════════════════════════════════════════════════ */

  function initPreviewHovers() {
    // Config JSON from liquid
    var config = {};
    var configEl = document.querySelector('[data-header-config]');
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        /* silently fail */
      }
    }

    // ── Shop Preview ──
    var shopLinks = document.querySelectorAll('[data-shop-link]');
    var shopPreview = document.querySelector('[data-shop-preview]');
    if (shopLinks.length && shopPreview) {
      var previewImg = shopPreview.querySelector('[data-preview-img]');
      var previewTag = shopPreview.querySelector('[data-preview-tag]');
      var previewTitle = shopPreview.querySelector('[data-preview-title]');
      var previewDesc = shopPreview.querySelector('[data-preview-desc]');
      var previewPrice = shopPreview.querySelector('[data-preview-price]');
      var previewBtn = shopPreview.querySelector('[data-preview-btn]');

      shopLinks.forEach(function(link) {
        link.addEventListener('mouseenter', function() {
          var idx = parseInt(link.getAttribute('data-shop-index'), 10) - 1;

          shopLinks.forEach(function(l) { l.classList.remove('is-active'); });
          link.classList.add('is-active');

          var imgSrc = link.querySelector('img');
          if (previewImg && imgSrc) {
            previewImg.src = imgSrc.src.replace(/_\d+x/, '_400x');
            previewImg.alt = imgSrc.alt;
          }

          var titleEl = link.querySelector('.dropdown__link-title');
          if (previewTitle && titleEl) previewTitle.textContent = titleEl.textContent;

          var descEl = link.querySelector('.dropdown__link-desc');
          if (previewDesc && descEl) previewDesc.textContent = descEl.textContent;

          var priceEl = link.querySelector('.dropdown__link-price');
          if (previewPrice && priceEl) previewPrice.textContent = priceEl.textContent;

          if (config.shopPreviewImages && config.shopPreviewImages[idx] && previewImg) {
            previewImg.src = config.shopPreviewImages[idx];
          }

          if (previewBtn) previewBtn.href = link.href;
        });
      });
    }

    // ── About Preview ──
    var aboutLinks = document.querySelectorAll('[data-about-link]');
    var aboutPreview = document.querySelector('[data-about-preview]');
    if (aboutLinks.length && aboutPreview) {
      var aboutPreviewImg = aboutPreview.querySelector('[data-preview-img]');
      var aboutPreviewTitle = aboutPreview.querySelector('[data-preview-title]');
      var aboutPreviewDesc = aboutPreview.querySelector('[data-preview-desc]');

      aboutLinks.forEach(function(link) {
        link.addEventListener('mouseenter', function() {
          var idx = parseInt(link.getAttribute('data-about-index'), 10) - 1;

          aboutLinks.forEach(function(l) { l.classList.remove('is-active'); });
          link.classList.add('is-active');

          var titleEl = link.querySelector('.dropdown__link-title');
          if (aboutPreviewTitle && titleEl) aboutPreviewTitle.textContent = titleEl.textContent;

          var descEl = link.querySelector('.dropdown__link-desc');
          if (aboutPreviewDesc && descEl) aboutPreviewDesc.textContent = descEl.textContent;

          if (config.aboutPreviewImages && config.aboutPreviewImages[idx] && aboutPreviewImg) {
            aboutPreviewImg.src = config.aboutPreviewImages[idx];
          }
        });
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════
     5. SEARCH OVERLAY
     ═══════════════════════════════════════════════════════════ */

  function initSearchOverlay() {
    var searchOverlay = document.getElementById('search-overlay');
    if (!searchOverlay) return;

    var searchToggle = document.querySelector('[data-search-toggle]');
    var searchTriggers = document.querySelectorAll('[data-search-trigger]');
    var closeBtn = searchOverlay.querySelector('.search-overlay__close');
    var backdrop = searchOverlay.querySelector('.search-overlay__backdrop');

    function openSearch() {
      searchOverlay.hidden = false;
      requestAnimationFrame(function() {
        searchOverlay.classList.add('active');
      });
      document.body.style.overflow = 'hidden';
      setTimeout(function() {
        var input = searchOverlay.querySelector('input[type="search"]');
        if (input) input.focus();
      }, 100);
    }

    function closeSearch() {
      searchOverlay.classList.remove('active');
      setTimeout(function() {
        searchOverlay.hidden = true;
        document.body.style.overflow = '';
      }, 350);
    }

    // Open triggers
    if (searchToggle) {
      searchToggle.addEventListener('click', openSearch);
    }
    searchTriggers.forEach(function(trigger) {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        openSearch();
      });
    });

    // Close triggers
    if (closeBtn) closeBtn.addEventListener('click', closeSearch);
    if (backdrop) backdrop.addEventListener('click', closeSearch);

    // Click on overlay/backdrop
    searchOverlay.addEventListener('click', function(e) {
      if (e.target === searchOverlay || e.target.classList.contains('search-overlay__backdrop')) {
        closeSearch();
      }
    });

    // ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && searchOverlay.classList.contains('active')) {
        closeSearch();
      }
    });

    // Also close dropdowns when opening search
    var origOpen = openSearch;
    searchOverlay._openSearch = origOpen;
  }

  /* ═══════════════════════════════════════════════════════════
     6. MOBILE MENU
     ═══════════════════════════════════════════════════════════ */

  function initMobileMenu() {
    var mobileToggle = document.querySelector('[data-mobile-toggle]');
    var mobileMenu = document.querySelector('[data-mobile-menu]');
    if (!mobileToggle || !mobileMenu) return;

    function toggleMobile() {
      var isOpen = mobileMenu.classList.contains('active');
      if (isOpen) {
        mobileMenu.classList.remove('active');
        var openIcon = mobileToggle.querySelector('.hamburger-open');
        var closeIcon = mobileToggle.querySelector('.hamburger-close');
        if (openIcon) openIcon.style.display = '';
        if (closeIcon) closeIcon.style.display = 'none';
      } else {
        mobileMenu.classList.add('active');
        var openIcon2 = mobileToggle.querySelector('.hamburger-open');
        var closeIcon2 = mobileToggle.querySelector('.hamburger-close');
        if (openIcon2) openIcon2.style.display = 'none';
        if (closeIcon2) closeIcon2.style.display = '';
      }
    }

    mobileToggle.addEventListener('click', toggleMobile);
    mobileMenu.addEventListener('click', function(e) {
      if (e.target === mobileMenu) toggleMobile();
    });

    // Scroll-to-top nav links also close mobile menu
    var scrollLinks = document.querySelectorAll('[data-scroll-top]');
    scrollLinks.forEach(function(link) {
      link.addEventListener('click', function() {
        window.scrollTo({ top: 0, behavior: 'instant' });
        if (mobileMenu.classList.contains('active')) {
          toggleMobile();
        }
      });
    });

    // Resize: close mobile menu when crossing breakpoint
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function() {
      var w = window.innerWidth;
      if ((lastWidth < 1024 && w >= 1024) || (lastWidth >= 1024 && w < 1024)) {
        mobileMenu.classList.remove('active');
      }
      lastWidth = w;
    });
  }

  /* ═══════════════════════════════════════════════════════════
     7. CART COUNT UPDATE
     ═══════════════════════════════════════════════════════════ */

  function initCartCount() {
    var cartCounts = document.querySelectorAll('[data-cart-count]');
    if (!cartCounts.length) return;

    function updateCartCount() {
      fetch(window.theme.routes.cart_url + '.js')
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          cartCounts.forEach(function(el) {
            el.textContent = cart.item_count;
            el.style.display = cart.item_count > 0 ? 'flex' : 'none';
          });
        })
        .catch(function() { /* silently fail */ });
    }

    updateCartCount();
    document.addEventListener('cart:updated', updateCartCount);
  }

  /* ═══════════════════════════════════════════════════════════
     8. CART DRAWER (simple redirect)
     ═══════════════════════════════════════════════════════════ */

  function initCartDrawer() {
    var cartTriggers = document.querySelectorAll('[data-cart-trigger]');
    cartTriggers.forEach(function(trigger) {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        window.location.href = '/cart';
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     9. ACCORDION (delegated)
     ═══════════════════════════════════════════════════════════ */

  function initAccordions() {
    // Use event delegation on accordion-group containers
    var groups = document.querySelectorAll('.accordion-group');
    groups.forEach(function(group) {
      group.addEventListener('click', function(e) {
        var trigger = e.target.closest('.accordion-trigger');
        if (!trigger) return;
        var item = trigger.closest('.accordion-item');
        if (!item) return;

        var isOpen = item.classList.contains('open');

        // Close siblings within the same group
        group.querySelectorAll('.accordion-item.open').forEach(function(openItem) {
          if (openItem !== item) openItem.classList.remove('open');
        });

        item.classList.toggle('open', !isOpen);
      });
    });

    // Standalone accordion items (no group)
    var standaloneItems = document.querySelectorAll('.accordion-item');
    standaloneItems.forEach(function(item) {
      if (item.closest('.accordion-group')) return; // already handled
      var trigger = item.querySelector('.accordion-trigger');
      if (!trigger) return;
      trigger.addEventListener('click', function() {
        item.classList.toggle('open');
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     10. PARALLAX EFFECT
     ═══════════════════════════════════════════════════════════ */

  function initParallax() {
    var parallaxElements = document.querySelectorAll('[data-parallax]');
    if (!parallaxElements.length) return;

    var ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(function() {
          parallaxElements.forEach(function(el) {
            var rect = el.getBoundingClientRect();
            var scrolled = window.innerHeight - rect.top;
            var rate = parseFloat(el.dataset.parallax) || 0.3;
            if (scrolled > 0 && rect.bottom > 0) {
              el.style.transform = 'translateY(' + (scrolled * rate) + 'px)';
            }
          });
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════════
     11. SMOOTH SCROLL FOR ANCHOR LINKS
     ═══════════════════════════════════════════════════════════ */

  function initSmoothScroll() {
    var anchors = document.querySelectorAll('a[href^="#"]');
    anchors.forEach(function(anchor) {
      anchor.addEventListener('click', function(e) {
        var targetId = this.getAttribute('href');
        if (targetId === '#') return;
        var target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     12. COUNT-UP ANIMATION
     ═══════════════════════════════════════════════════════════ */

  function initCountUp() {
    var counters = document.querySelectorAll('[data-count-up]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var target = parseInt(el.dataset.countUp, 10);
          var suffix = el.dataset.countSuffix || '';
          var duration = parseInt(el.dataset.countDuration, 10) || 2000;
          var startTime = performance.now();

          function update(currentTime) {
            var elapsed = currentTime - startTime;
            var progress = Math.min(elapsed / duration, 1);
            var easeProgress = 1 - Math.pow(1 - progress, 3);
            var current = Math.floor(easeProgress * target);
            el.textContent = current.toLocaleString() + suffix;

            if (progress < 1) {
              requestAnimationFrame(update);
            } else {
              el.textContent = target.toLocaleString() + suffix;
            }
          }

          requestAnimationFrame(update);
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach(function(counter) { observer.observe(counter); });
  }

  /* ═══════════════════════════════════════════════════════════
     13. VIDEO HOVER PLAY
     ═══════════════════════════════════════════════════════════ */

  function initVideoHover() {
    document.querySelectorAll('[data-video-hover]').forEach(function(container) {
      var video = container.querySelector('video');
      if (!video) return;

      container.addEventListener('mouseenter', function() {
        video.play().catch(function() {});
      });
      container.addEventListener('mouseleave', function() {
        video.pause();
        video.currentTime = 0;
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     14. HOME HERO SCROLL ANIMATION
     ═══════════════════════════════════════════════════════════ */

  function initHeroAnimation() {
    var heroSection = document.querySelector('[data-hero-section]');
    if (!heroSection) return;

    var bgLayer = heroSection.querySelector('[data-hero-bg]');
    var videoContainer = heroSection.querySelector('[data-hero-video]');
    var headline = heroSection.querySelector('[data-hero-headline]');
    var subline = heroSection.querySelector('[data-hero-subline]');
    var tagline = heroSection.querySelector('[data-hero-tagline]');
    var buttons = heroSection.querySelector('[data-hero-buttons]');

    function updateHeroAnimation() {
      var rect = heroSection.getBoundingClientRect();
      var windowHeight = window.innerHeight;
      var scrollProgress = Math.max(0, Math.min(1, -rect.top / windowHeight));

      if (bgLayer) {
        bgLayer.style.opacity = String(1 - scrollProgress * 1.5);
      }

      if (videoContainer) {
        var yOffset = (1 - scrollProgress) * 100;
        var scale = 0.92 + scrollProgress * 0.08;
        var radius = 40 * (1 - scrollProgress);
        videoContainer.style.transform = 'translateY(' + yOffset + '%) scale(' + scale + ')';
        videoContainer.style.borderRadius = radius + 'px';
      }

      if (headline) {
        var r = Math.round(26 + (255 - 26) * scrollProgress);
        var g = Math.round(43 + (255 - 43) * scrollProgress);
        var b = Math.round(61 + (255 - 61) * scrollProgress);
        headline.style.color = 'rgb(' + r + ',' + g + ',' + b + ')';
      }

      if (subline) {
        var alpha = 0.6 + scrollProgress * 0.25;
        subline.style.color = 'rgba(255, 255, 255, ' + alpha + ')';
      }

      if (tagline) {
        tagline.style.opacity = String(1 - scrollProgress * 2);
      }

      if (buttons) {
        buttons.style.opacity = String(1 - scrollProgress * 2);
      }
    }

    var ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(function() {
          updateHeroAnimation();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    updateHeroAnimation();
  }

  /* ═══════════════════════════════════════════════════════════
     15. SOCIAL VIDEO CAROUSEL
     ═══════════════════════════════════════════════════════════ */

  function initSocialCarousel() {
    var carousel = document.querySelector('[data-social-carousel]');
    if (!carousel) return;

    var track = carousel.querySelector('[data-carousel-track]');
    var slides = carousel.querySelectorAll('[data-carousel-slide]');
    var prevBtn = carousel.querySelector('[data-carousel-prev]');
    var nextBtn = carousel.querySelector('[data-carousel-next]');
    var dotsContainer = carousel.querySelector('[data-carousel-dots]');

    if (!track || slides.length === 0) return;

    var currentIndex = 0;
    var totalSlides = slides.length;

    function getSlidesPerView() {
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 768) return 2;
      return 1;
    }

    function updateCarousel() {
      var slidesPerView = getSlidesPerView();
      var slideWidth = 100 / slidesPerView;
      var maxIndex = Math.max(0, totalSlides - slidesPerView);
      var clampedIndex = Math.min(currentIndex, maxIndex);

      track.style.transform = 'translateX(-' + (clampedIndex * slideWidth) + '%)';

      if (dotsContainer) {
        var dots = dotsContainer.querySelectorAll('[data-carousel-dot]');
        dots.forEach(function(dot, i) {
          dot.classList.toggle('active', i === clampedIndex);
        });
      }

      if (prevBtn) prevBtn.disabled = clampedIndex === 0;
      if (nextBtn) nextBtn.disabled = clampedIndex >= maxIndex;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        currentIndex = Math.max(0, currentIndex - 1);
        updateCarousel();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        var slidesPerView = getSlidesPerView();
        var maxIndex = totalSlides - slidesPerView;
        currentIndex = Math.min(maxIndex, currentIndex + 1);
        updateCarousel();
      });
    }

    // Touch/swipe support
    var touchStartX = 0;
    var touchEndX = 0;

    track.addEventListener('touchstart', function(e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    track.addEventListener('touchend', function(e) {
      touchEndX = e.changedTouches[0].screenX;
      var diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          var spv = getSlidesPerView();
          var maxIdx = totalSlides - spv;
          currentIndex = Math.min(maxIdx, currentIndex + 1);
        } else {
          currentIndex = Math.max(0, currentIndex - 1);
        }
        updateCarousel();
      }
    }, { passive: true });

    // Video hover play on slides
    slides.forEach(function(slide) {
      var video = slide.querySelector('video');
      if (!video) return;

      slide.addEventListener('mouseenter', function() {
        video.play().catch(function() {});
        slide.classList.add('playing');
      });
      slide.addEventListener('mouseleave', function() {
        video.pause();
        video.currentTime = 0;
        slide.classList.remove('playing');
      });
    });

    // Debounced resize handler
    var resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateCarousel, 150);
    });

    updateCarousel();
  }

  function initCollectionProductGuards() {
    if (window.location.pathname.indexOf('/collections/') !== 0) return;

    document.querySelectorAll('.pepax-products-grid a[href*="/products/nmn"]').forEach(function(link) {
      var card = link.closest('.product-card-wrapper') || link.closest('.pepax-product-card') || link;
      if (card && card.parentNode) card.parentNode.removeChild(card);
    });
  }

  /* ═══════════════════════════════════════════════════════════
     16. MASTER INITIALIZE
     ═══════════════════════════════════════════════════════════ */

  function init() {
    // Enable JS-enhanced CSS
    document.documentElement.classList.add('js-enabled');

    // Base features
    initRevealAnimations();
    initAccordions();
    initParallax();
    initSmoothScroll();
    initCountUp();
    initVideoHover();

    // Header features
    initHeaderDropdowns();
    initPreviewHovers();
    initSearchOverlay();
    initMobileMenu();
    initCartCount();
    initCartDrawer();

    // Conditional: Hero animation (only on pages with hero section)
    initHeroAnimation();

    // Conditional: Social carousel (only on pages with carousel)
    initSocialCarousel();
    initCollectionProductGuards();
  }

  onReady(init);
})();


/* ── Lazy-loaded image fade-in handler ── */
(function() {
  function handleLazyImages() {
    document.querySelectorAll('img[loading="lazy"]').forEach(function(img) {
      if (img.complete && img.naturalHeight > 0) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', function() { img.classList.add('loaded'); });
        img.addEventListener('error', function() { img.classList.add('loaded'); });
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleLazyImages);
  } else {
    handleLazyImages();
  }
})();
